"use client"

import * as React from "react"
import type { AnyExtension } from "@tiptap/core"
import { EditorContent, EditorContext, useEditor } from "@tiptap/react"

// --- Tiptap Core Extensions ---
import { StarterKit } from "@tiptap/starter-kit"
import { Image } from "@tiptap/extension-image"
import { TaskItem, TaskList } from "@tiptap/extension-list"
import { TextAlign } from "@tiptap/extension-text-align"
import { Typography } from "@tiptap/extension-typography"
import { Placeholder } from "@tiptap/extension-placeholder"
import { Markdown } from "tiptap-markdown"

// --- UI Primitives ---
import { Button } from "@/components/tiptap-ui-primitive/button"
import { Spacer } from "@/components/tiptap-ui-primitive/spacer"
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from "@/components/tiptap-ui-primitive/toolbar"

// --- Tiptap Node ---
import { ImageUploadNode } from "@/components/tiptap-node/image-upload-node/image-upload-node-extension"
import { HorizontalRule } from "@/components/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension"
import "@/components/tiptap-node/blockquote-node/blockquote-node.scss"
import "@/components/tiptap-node/code-block-node/code-block-node.scss"
import "@/components/tiptap-node/horizontal-rule-node/horizontal-rule-node.scss"
import "@/components/tiptap-node/list-node/list-node.scss"
import "@/components/tiptap-node/image-node/image-node.scss"
import "@/components/tiptap-node/heading-node/heading-node.scss"
import "@/components/tiptap-node/paragraph-node/paragraph-node.scss"

// --- Tiptap UI ---
import { HeadingDropdownMenu } from "@/components/tiptap-ui/heading-dropdown-menu"
import { ListDropdownMenu } from "@/components/tiptap-ui/list-dropdown-menu"
import { BlockquoteButton } from "@/components/tiptap-ui/blockquote-button"
import { CodeBlockButton } from "@/components/tiptap-ui/code-block-button"
import {
  LinkPopover,
  LinkContent,
  LinkButton,
} from "@/components/tiptap-ui/link-popover"
import { MarkButton } from "@/components/tiptap-ui/mark-button"
import { TextAlignButton } from "@/components/tiptap-ui/text-align-button"
import { UndoRedoButton } from "@/components/tiptap-ui/undo-redo-button"

// --- Icons ---
import { ArrowLeftIcon } from "@/components/tiptap-icons/arrow-left-icon"
import { LinkIcon } from "@/components/tiptap-icons/link-icon"

// --- Hooks ---
import { useIsMobile } from "@/hooks/use-mobile"
import { useWindowSize } from "@/hooks/use-window-size"
import { useCursorVisibility } from "@/hooks/use-cursor-visibility"

// --- Slash command palette ---
import { SlashCommandPopover } from "@/components/tiptap-ui/block-insert-menu"

// --- Selection toolbar ---
import { SelectionToolbar } from "@/components/tiptap-ui/selection-toolbar/SelectionToolbar"

// --- Mention extension ---
import { buildMentionExtension } from "editor"
import type { MentionSuggestionConfig } from "editor"

// --- Lib ---
import { handleImageUpload, MAX_FILE_SIZE } from "@/lib/tiptap-utils"

// --- Styles ---
import "@/components/tiptap-templates/simple/simple-editor.scss"

// Removed default sample content import to avoid prefilled text

const MainToolbarContent = ({
  onLinkClick,
  isMobile,
}: {
  onLinkClick: () => void
  isMobile: boolean
}) => {
  return (
    <>
      <Spacer />

      <ToolbarGroup>
        <UndoRedoButton action="undo" />
        <UndoRedoButton action="redo" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <HeadingDropdownMenu levels={[1, 2, 3, 4]} portal={isMobile} />
        <ListDropdownMenu
          types={["bulletList", "orderedList", "taskList"]}
          portal={isMobile}
        />
        <BlockquoteButton />
        <CodeBlockButton />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="bold" />
        <MarkButton type="italic" />
        <MarkButton type="strike" />
        <MarkButton type="code" />
        {!isMobile ? <LinkPopover /> : <LinkButton onClick={onLinkClick} />}
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <TextAlignButton align="left" />
        <TextAlignButton align="center" />
        <TextAlignButton align="right" />
        <TextAlignButton align="justify" />
      </ToolbarGroup>

      <ToolbarSeparator />
      <Spacer />
    </>
  )
}

const MobileToolbarContent = ({
  onBack,
}: {
  onBack: () => void
}) => (
  <>
    <ToolbarGroup>
      <Button data-style="ghost" onClick={onBack}>
        <ArrowLeftIcon className="tiptap-button-icon" />
        <LinkIcon className="tiptap-button-icon" />
      </Button>
    </ToolbarGroup>

    <ToolbarSeparator />

    <LinkContent />
  </>
)

export interface SimpleEditorProps {
  content?: unknown;
  onReady?: (editor: import("@tiptap/react").Editor) => void;
  onUpdate?: (payload: { editor: import("@tiptap/react").Editor; markdown?: string; json: unknown; text: string }) => void;
  /**
   * Extra classes for the `.simple-editor-wrapper` div.
   * The default SCSS pins the wrapper to `height: 70vh` for full-page admin
   * editors. When embedding inside a constrained container (e.g. a modal),
   * pass `!h-full !max-h-full` to override that.
   */
  wrapperClassName?: string;
  /**
   * Hide the static formatting toolbar at the top of the editor.
   * Useful in modals/composers where the toolbar should not be permanently
   * visible.
   */
  hideToolbar?: boolean;
  /**
   * Placeholder text shown when the editor is empty. Helps users
   * distinguish the body editor from surrounding empty space.
   */
  placeholder?: string;
  /**
   * Optional mention suggestion config. When provided, the @mention
   * extension is added so users can tag studio members by typing @.
   */
  mentionSuggestion?: MentionSuggestionConfig;
  /**
   * Show a floating toolbar above the text selection with inline
   * formatting options (bold, italic, strike, code, lists). Useful
   * in modals/composers where the static toolbar is hidden.
   */
  showSelectionToolbar?: boolean;
}

export function SimpleEditor({ content: initialContent, onReady, onUpdate, wrapperClassName, hideToolbar = false, placeholder, mentionSuggestion, showSelectionToolbar = false }: SimpleEditorProps) {
  const isMobile = useIsMobile()
  const { height } = useWindowSize()
  const [mobileView, setMobileView] = React.useState<
    "main" | "link"
  >("main")
  const toolbarRef = React.useRef<HTMLDivElement>(null)
  const wrapperRef = React.useRef<HTMLDivElement>(null)

  const editor = useEditor({
    immediatelyRender: false,
    shouldRerenderOnTransaction: false,
    editorProps: {
      attributes: {
        autocomplete: "off",
        autocorrect: "off",
        autocapitalize: "off",
        "aria-label": "Main content area, start typing to enter text.",
        class: "tiptap ProseMirror simple-editor",
      },
    },
    extensions: [
      StarterKit.configure({
        horizontalRule: false,
        link: {
          openOnClick: false,
          enableClickSelection: true,
        },
      }),
      HorizontalRule,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Image,
      Typography,
      Markdown.configure({
        html: false,
        breaks: true,
        linkify: true,
        bulletListMarker: "-",
        transformCopiedText: true,
        transformPastedText: true,
      }),
      ImageUploadNode.configure({
        accept: "image/*",
        maxSize: MAX_FILE_SIZE,
        limit: 3,
        upload: handleImageUpload,
        onError: (error) => console.error("Upload failed:", error),
      }),
      ...(placeholder
        ? [
            Placeholder.configure({
              // Only render the placeholder on the very first node when
              // the whole document is empty. Any subsequent empty
              // paragraphs (e.g. from pressing Enter) render with no
              // placeholder at all — no "ghost" repeating on every
              // new line. Focus-hide is handled in paragraph-node.scss.
              placeholder: ({ editor, pos }) =>
                pos === 0 && editor.isEmpty ? placeholder : "",
              showOnlyWhenEditable: true,
              showOnlyCurrent: false,
            }),
          ]
        : []),
      ...(mentionSuggestion ? [buildMentionExtension(mentionSuggestion)] : []),
    ] as unknown as AnyExtension[],
    // Initialize content once from props; do not sync on each change to avoid cursor jumps
    ...(initialContent != null ? { content: initialContent as any } : {}),
    onUpdate({ editor }) {
      const json = editor.getJSON();
      const text = editor.getText();
      const markdown = (editor.storage as any)?.markdown?.getMarkdown?.();
      onUpdate?.({ editor, markdown, json, text });
    },
  })

  const rect = useCursorVisibility({
    editor,
    overlayHeight: toolbarRef.current?.getBoundingClientRect().height ?? 0,
  })

  React.useEffect(() => {
    if (editor && onReady) {
      onReady(editor)
    }
  }, [editor, onReady])

  React.useEffect(() => {
    if (!isMobile && mobileView !== "main") {
      setMobileView("main")
    }
  }, [isMobile, mobileView])

  return (
    <div
      ref={wrapperRef}
      className={
        wrapperClassName
          ? `simple-editor-wrapper relative ${wrapperClassName}`
          : "simple-editor-wrapper relative"
      }
    >
      <EditorContext.Provider value={{ editor }}>
        {!hideToolbar && (
          <Toolbar
            ref={toolbarRef}
            style={{
              ...(isMobile
                ? {
                    bottom: `calc(100% - ${height - rect.y}px)`,
                  }
                : {}),
            }}
          >
            {mobileView === "main" ? (
              <MainToolbarContent
                onLinkClick={() => setMobileView("link")}
                isMobile={isMobile}
              />
            ) : (
              <MobileToolbarContent
                onBack={() => setMobileView("main")}
              />
            )}
          </Toolbar>
        )}

        <EditorContent
          editor={editor}
          role="presentation"
          className="simple-editor-content"
        />

        {/* Floating slash-command palette. Renders only when the user
            types "/" in the editor body; otherwise it's a no-op.
            Anchored to the wrapper (relative-positioned above) so the
            popover survives transformed ancestors like Radix Dialog. */}
        <SlashCommandPopover editor={editor} containerRef={wrapperRef} />

        {showSelectionToolbar && <SelectionToolbar editor={editor} />}
      </EditorContext.Provider>
    </div>
  )
}
